import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import posthog from 'posthog-js'
import { Analytics } from '@vercel/analytics/react'
// Wave UI-1: Inter Variable as the base sans font. Imported once here so
// no font-family literal needs to live in components. The base font and
// tabular-numerals for digits are wired in index.css.
// DASH-VISUAL-1 "Night Shift": Manrope carries the interface, Fraunces
// carries the numbers. See docs/design/DASHBOARD-VISUAL-SYSTEM.md.
import '@fontsource-variable/manrope/index.css'
import '@fontsource-variable/fraunces/index.css'
import './index.css'
import App from './App.tsx'
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary'
import { ErrorBoundary } from './components/ErrorBoundary'
import { captureRefFromUrl } from './lib/referralAttribution'
import { isDynamicImportError, reloadOnceForStaleChunk } from './lib/staleChunk'
import { captureException, handOver, watchForEarlyErrors } from './lib/report'

/**
 * THE-HOME-LOADS-FAST-2 — THE FIRST LINE THE APP RUNS, and it costs nothing.
 *
 * Ruled: error reporting stays and loads after the home paints, with a tiny
 * early handler holding what happens in between. This is that handler, and it
 * is registered before anything else in this file so the window it covers
 * starts at the top rather than wherever the imports happen to settle.
 */
watchForEarlyErrors(window)

// Stale chunk auto-recovery. After a deploy, a tab opened against the previous
// build references old hashed route-chunk filenames that no longer exist on the
// host. Vite fires vite:preloadError when a modulepreload fails; we suppress the
// default throw and reload once (guarded) so the user silently lands on the
// fresh build and the route they were navigating to. See src/lib/staleChunk.ts.
window.addEventListener('vite:preloadError', (event) => {
  const payload = (event as Event & { payload?: unknown }).payload
  if (isDynamicImportError(payload)) {
    event.preventDefault()
    // RELIABILITY-1: REPORT BEFORE RELOADING. preventDefault() stops the throw,
    // so Sentry's global handler never sees this, and reloadOnce...() navigates
    // away immediately — anything after it never runs. Without this line the
    // most common cause of "couldn't load" is completely invisible.
    try {
      captureException(
        payload instanceof Error ? payload : new Error(String(payload ?? 'vite:preloadError')),
        {
          tags: { boundary: 'preload', boundary_kind: 'stale_chunk' },
          extra: { path: window.location.pathname },
        },
      )
    } catch {
      // Defensive only. This listener is REGISTERED above Sentry.init, but it
      // FIRES when a lazy route is navigated to, which is always long after
      // init, so in practice the report lands. The reload happens either way,
      // which is what the user needs.
    }
    reloadOnceForStaleChunk()
  }
})

/**
 * THE-HOME-LOADS-FAST-2 — THE REPORTER, ONCE THE PAGE HAS PAINTED.
 *
 * `Sentry.init` ran here, at the top of the entry, which put about 440 KB of
 * source in front of every first screen. It runs in `lib/reporter.ts` now, a
 * dynamic import asked for when the browser says it has a moment, and the seam
 * above hands it everything that happened in the meantime.
 *
 * `requestIdleCallback` where there is one and a timeout where there is not,
 * and the timeout is also the ceiling handed to it: a page that never goes
 * idle still reports, two seconds in.
 */
const REPORTER_CEILING_MS = 2000
const attachReporter = () =>
  void import('./lib/reporter')
    .then((m) => m.loadReporter())
    .then(handOver)
    .catch(() => {
      // The reporter could not be loaded. The held errors stay held, and the
      // page is not made worse by telemetry that did not arrive.
    })
if ('requestIdleCallback' in window) {
  ;(
    window as unknown as {
      requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => void
    }
  ).requestIdleCallback(attachReporter, { timeout: REPORTER_CEILING_MS })
} else {
  setTimeout(attachReporter, REPORTER_CEILING_MS)
}

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com'

if (POSTHOG_KEY && import.meta.env.PROD) {
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: 'identified_only',
    capture_pageview: true,
    capture_pageleave: true,
    autocapture: true,
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: true,
    },
    loaded: (ph) => {
      if (import.meta.env.DEV) ph.debug()
    },
  })
}

// Phase R4.0: capture ?ref=<affiliate_username> before React mounts.
// Idempotent + cheap — runs once per page load. The captured ref is
// applied to the user's sub_account on first signup (AuthProvider
// hydration) and on every "Add business" creation
// (CreateSubAccountModal).
captureRefFromUrl()

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {/* THE-HOME-LOADS-FAST-2 — THE OUTER BOUNDARY IS NO LONGER SENTRY'S, and
        it says and does exactly what it said and did. It was the only reason
        the reporter had to be a static import at the top of this file: a
        component cannot be awaited. It is the same primitive the inner
        boundary already uses, reporting through the same seam, with the same
        words nobody normally sees. */}
    <ReactErrorBoundary
      fallback={<div>Something went wrong. The team has been notified.</div>}
      onError={(error) =>
        captureException(error, { tags: { boundary: 'root', boundary_kind: 'render' } })
      }
    >
      <ErrorBoundary>
        <App />
        <Analytics />
      </ErrorBoundary>
    </ReactErrorBoundary>
  </StrictMode>,
)
